import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { isValidHttpUrl, timezoneToOffset } from '@/shared/lib';
import { convertToMomentJs } from '@/shared/lib/aiUtils';

import { SAVED_SRIPS_IMAGES } from '@/widgets/strips-management/ui/handler';
import type { ThumbnailEventProps } from './thumbnailEvent';

/**
 * Backs the thumbnail cell showing an event's first captured strip in the strips table. It resolves that image to a blob URL, through the pre-download cache for remote URLs and directly for inline markup, resets the error flag whenever the image changes, and formats the strip start or the event start as MM/DD, HH:mm:ss in the event's timezone.
 *
 * @param props - Thumbnail props whose event carries stripImgs, timezone, stripStart and eventStart.
 * @returns An object with the image error flag and its setter, the resolved strip URL and the formatted strip start time.
 */
const useThumbnailEvent = ({ event }: Pick<ThumbnailEventProps, 'event'>) => {
  const [isError, setIsError] = useState(false);
  const [stripUrl, setStripUrl] = useState('');

  const { stripImgs, timezone, stripStart, eventStart } = event || {};

  // const findCachedImage = (stripImg) => {
  //   const cachedImg = _.find(SAVED_SRIPS_IMAGES, x => x.baseUrl === stripImg);
  //   return cachedImg?.objectUrl ? `data:image/svg+xml;base64,${btoa(cachedImg?.objectUrl)}` : stripImg;
  // };

  /**
   * Resolves a thumbnail's remote strip image URL to a local blob URL when its markup was pre-downloaded. It looks the URL up in the shared SAVED_SRIPS_IMAGES cache and wraps the cached SVG markup in a blob URL, falling back to the original URL on a miss.
   *
   * @param stripImg - Strip image URL to resolve.
   * @returns A blob URL for the cached SVG, or the original URL when it was not cached.
   */
  const findCachedImage = (stripImg: any) => {
    const cachedImg = _.find(SAVED_SRIPS_IMAGES, (x) => x.baseUrl === stripImg);
    // return cachedImg?.objectUrl ? `data:image/svg+xml;base64,${btoa(cachedImg?.objectUrl)}` : stripImg;
    if (cachedImg?.objectUrl) {
      const svgBlob = new Blob([cachedImg?.objectUrl], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      return svgUrl;
    }
    return stripImg;
  };

  useEffect(() => {
    setIsError(false);
    const stripImg = stripImgs?.[0];
    if (stripImg) {
      // const formatImg = isValidHttpUrl(stripImg) ? findCachedImage(stripImg) : `data:image/svg+xml;base64,${btoa(stripImg)}`;
      const svgBlob = new Blob([stripImg], { type: 'image/svg+xml' });
      const svgUrl = URL.createObjectURL(svgBlob);
      const formatImg = isValidHttpUrl(stripImg) ? findCachedImage(stripImg) : svgUrl;
      setStripUrl(formatImg);
    } else {
      setStripUrl('');
    }
  }, [stripImgs?.[0]]);

  const stripStartTime = useMemo(
    () =>
      stripStart
        ? convertToMomentJs(stripStart, timezoneToOffset(timezone))?.format('MM/DD, HH:mm:ss')
        : eventStart
          ? convertToMomentJs(eventStart, timezoneToOffset(timezone))?.format('MM/DD, HH:mm:ss')
          : '',
    [timezone, stripStart, eventStart],
  );

  return {
    isError,
    setIsError,
    stripUrl,
    stripStartTime,
  };
};

export default useThumbnailEvent;
